import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import axiosInstance from 'config/axiosInstance';
import { businessZoneLabel, getBusinessTimeZone, setBusinessTimeZone } from 'config/time';
import { useUtcOffsetChange } from 'reusable/useUtcOffsetChange';

/**
 * 会社の時間帯を管理画面の全体へ渡す。EV-116 段階2（TZ-AC9）。
 *
 * - 時間帯の正本はサーバーの会社の設定（プロフィールの company_info.time_zone）。端末の時間帯は使わない。
 * - ログインの後、業務の画面を描く前にプロフィールを読む（前の時間帯で一度データを取らない）。
 * - 画面への復帰（focus・visibilitychange）で読み直す。ほかの管理者が時間帯を変えたら、ここで反映する。
 * - 読めない・ブラウザで使えない名前は既定（Asia/Tokyo）。どの時間帯かは集計の基準の表示で分かる。
 *
 * 時間帯が変わったら zone が変わる。レイアウトは zone を key にして業務の画面を作り直し、
 * 各画面が新しい境界でデータを取り直す。
 */
type CompanyTimeZoneState = {
  /** 今の業務時間帯（config/time と同じ値）。 */
  zone: string;
  /** プロフィールを1回読み終えたか（失敗を含む）。 */
  ready: boolean;
  /**
   * ブラウザの Intl が受け付けなかった会社の時間帯の名前（そのときは既定の Asia/Tokyo で計算する）。
   * 使えるとき・会社の時間帯が無いときは null。TZ-AC20（2026-10-11 PM 判断）。
   */
  unsupported: string | null;
  /** 時間帯を変える（会社の設定の画面が保存の後に使う）。 */
  apply: (zone: string | null | undefined) => void;
  /** プロフィールを読み直す。 */
  refresh: () => Promise<void>;
};

const CompanyTimeZoneContext = createContext<CompanyTimeZoneState>({
  zone: getBusinessTimeZone(),
  ready: true,
  unsupported: null,
  apply: (zone) => {
    setBusinessTimeZone(zone);
  },
  refresh: async () => {},
});

export const useCompanyTimeZone = () => useContext(CompanyTimeZoneContext);

export const CompanyTimeZoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<{ zone: string; ready: boolean; unsupported: string | null }>(
    () => ({ zone: getBusinessTimeZone(), ready: false, unsupported: null })
  );
  // 古い応答で新しい結果を上書きしない（復帰が続いたとき・保存の後に古い読み直しが届いたとき）。
  // 読み直しを始めるたび、また保存の応答で時間帯を変えるたびに進める世代。
  const latest = useRef(0);

  const applyZone = useCallback((zone: string | null | undefined) => {
    const usable = setBusinessTimeZone(zone);
    const actual = getBusinessTimeZone();
    // 会社の時間帯があるのにブラウザが知らない名前なら、既定で計算していることを注記する。
    const unsupported = !usable && typeof zone === 'string' && zone !== '' ? zone : null;
    setState((prev) =>
      prev.ready && prev.zone === actual && prev.unsupported === unsupported
        ? prev
        : { zone: actual, ready: true, unsupported }
    );
  }, []);

  const refresh = useCallback(async () => {
    const request = ++latest.current;
    try {
      const response = await axiosInstance.get('/v1/profile/');
      if (request !== latest.current) return;
      applyZone(response?.data?.company_info?.time_zone);
    } catch (error) {
      if (request !== latest.current) return;
      // 読めないときは今の時間帯のまま（最初は既定の Asia/Tokyo）。
      setState((prev) => (prev.ready ? prev : { ...prev, ready: true }));
    }
  }, [applyZone]);

  /**
   * 保存の応答（正本）で時間帯を変える。RV-275（PR #216 の自動レビュー）。それより前に始まった
   * 読み直し（初回・復帰）の応答は古いので、世代を進めて捨てる。以前は世代を進めず、古い応答が
   * 後から届くと前の時間帯で上書きし、次の読み直しまで前の時間帯の境界で問い合わせていた。
   */
  const apply = useCallback(
    (zone: string | null | undefined) => {
      latest.current += 1;
      applyZone(zone);
    },
    [applyZone]
  );

  useEffect(() => {
    refresh();
    const onFocus = () => {
      refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      latest.current += 1;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  return (
    <CompanyTimeZoneContext.Provider value={{ ...state, apply, refresh }}>
      {children}
    </CompanyTimeZoneContext.Provider>
  );
};

/**
 * 集計の基準の時間帯（TZ-AC11）。「集計の基準: Asia/Tokyo (UTC+09:00)」。差は今の時点の値。
 *
 * RV-280（PR #216 の自動レビュー）。差を描画のときにしか計算していなかったので、画面を開いたまま
 * 夏時間に入る（抜ける）と古いまま残った。次の差の切り替わりの時刻に描き直す（useUtcOffsetChange。
 * 上限より先の切り替わりは上限で置き直す。RV-284）。休止などでタイマーが遅れたときのため、
 * 画面への復帰（focus・visibilitychange）でも描き直す。
 */
export const BusinessTimeZoneNote: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const { zone } = useCompanyTimeZone();
  const [, setTick] = useState(0);
  const redraw = useCallback(() => setTick((tick) => tick + 1), []);

  useUtcOffsetChange(zone, redraw);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') redraw();
    };
    window.addEventListener('focus', redraw);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', redraw);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [redraw]);

  return (
    <p className={`text-caption text-neutral/100 ${className}`} data-testid="business-time-zone">
      {t('time-zone.basis', { ns: 'common', zone: businessZoneLabel(zone) })}
    </p>
  );
};

/**
 * ブラウザが会社の時間帯を知らないときの注記（TZ-AC20、文言は 2026-10-11 PM 承認）。
 * 集計の基準のすぐ下に出す。読み上げで伝わるよう role="status"。使えるときは何も出さない。
 */
export const BusinessTimeZoneWarning: React.FC = () => {
  const { t } = useTranslation('common');
  const { unsupported } = useCompanyTimeZone();
  if (!unsupported) return null;
  return (
    <p role="status" className="text-caption text-error">
      {t('time-zone.unsupported', { ns: 'common', name: unsupported })}
    </p>
  );
};
